import { useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-leaflet';
import Supercluster from 'supercluster';
import RenderMarkerSuperCluster from './RenderMarkerSuperCluster';
import { LatLngBounds, Map } from 'leaflet';

interface Properties {
    data: any;
    options: any;
}

const updateMapBoundsAndZoom = (
    inputMap: Map,
    inputSetBounds: React.Dispatch<React.SetStateAction<GeoJSON.BBox>>,
    inputSetZoom: React.Dispatch<React.SetStateAction<number>>
) => {
    if (inputMap) {
        const b: LatLngBounds = inputMap.getBounds()

        inputSetBounds([
            b.getSouthWest().lng,
            b.getSouthWest().lat,
            b.getNorthEast().lng,
            b.getNorthEast().lat
        ])

        inputSetZoom(inputMap.getZoom())
    }
}

const ClusterWithSuperCluster = (properties: Properties) => {
    const mapContext = useMap();
    const [dataCluster, setDataCluster] = useState([]) as any;
    const [mapBounds, setMapBounds] = useState<GeoJSON.BBox>([0, 0, 0, 0]);
    const [mapZoom, setMapZoom] = useState<number>(0);
    const [superclusterRef, setSuperclusterRef] = useState<{ [key: string]: any; } | undefined>();

    const _memoizeDataCluster = useMemo(() => {
        return dataCluster
    }, [dataCluster])
    const memoizeSuperclusterRef = useMemo(() => {
        return superclusterRef
    }, [superclusterRef])

    const updateCluster = () => {
        if (!properties.data || !Array.isArray(properties.data)) {
            return;
        }
        
        const pointList = properties.data.map((item: any) => ({
            type: 'Feature' as const,
            properties: {
                cluster: false
            },
            geometry: {
                type: 'Point' as const,
                coordinates: [item.lon ?? item.long, item.lat]
            },
            markerData: item
        }))

        const initSupercluster = new Supercluster({ radius: 40, maxZoom: 16 });

        initSupercluster.load(pointList)

        const getClusters = initSupercluster.getClusters(mapBounds, mapZoom)

        setSuperclusterRef(initSupercluster)
        setDataCluster(getClusters)
    }

    useEffect(() => {
        if (mapContext) {
            if (!mapZoom) {
                updateMapBoundsAndZoom(mapContext, setMapBounds, setMapZoom)
            }

            mapContext.on('zoom drag', () => {
                updateMapBoundsAndZoom(mapContext, setMapBounds, setMapZoom)
            })
        }
    }, [mapContext])

    useEffect(() => {
        if (mapBounds && mapZoom) updateCluster()
    }, [mapBounds, mapZoom])

    // Comprehensive defensive guards after all hooks
    if (!properties) {
        console.error("[ClusterWithSuperCluster] properties is null or undefined");
        return <div>Loading...</div>;
    }
    
    if (typeof properties !== "object") {
        console.error("[ClusterWithSuperCluster] properties is not an object:", typeof properties, properties);
        return <div>Loading...</div>;
    }
    
    // Ensure properties.options exists and is an object
    if (!properties.options) {
        console.warn("[ClusterWithSuperCluster] properties.options is null/undefined, using empty object");
        properties.options = {};
    }
    
    if (typeof properties.options !== "object") {
        console.warn("[ClusterWithSuperCluster] properties.options is not an object, using empty object");
        properties.options = {};
    }

    return <RenderMarkerSuperCluster superclusterRef={memoizeSuperclusterRef} data={properties.data} options={properties.options || {}}/>;
}

export default ClusterWithSuperCluster